import React, { useState, useEffect } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Link as RouterLink } from 'react-router-dom';
import { DateTime } from 'luxon';

import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Tooltip from '@material-ui/core/Tooltip';
import Button from '@material-ui/core/Button';
import Link from '@material-ui/core/Link';
import Chip from '@material-ui/core/Chip';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';

import TablePagination from '@material-ui/core/TablePagination';

import AddIcon from '@material-ui/icons/Add';

import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';
import { container } from 'app/styles/new-layout';

import useApi from 'app/context/useApi';

const useStyles = makeStyles(theme => ({
    container: {
        ...container(theme),
        padding: theme.spacing(3)
    },
    paper: {
        ...paper(theme),
        position: 'relative'
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    marginRight: {
        marginRight: theme.spacing(3)
    },
    marginBottom: {
        marginBottom: 12
    },
    empty: {
        paddingTop: theme.spacing(5),
        paddingBottom: theme.spacing(5),
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    published: {
        backgroundColor: theme.palette.success.dark,
        color: '#fff',
        marginRight: theme.spacing(3),
        textTransform: 'capitalize'
    },
    scheduled: {
        backgroundColor: theme.palette.warning.dark,
        color: '#fff',
        marginRight: theme.spacing(3),
        textTransform: 'capitalize'
    },
    draft: {
        marginRight: theme.spacing(3),
        textTransform: 'capitalize'
    },
    nowrap: {
        whiteSpace: 'nowrap',
    }
}));

export default function Articles(props) {

    const classes = useStyles();
    const api = useApi();
    const now = DateTime.local();

    const [ articles, setArticles ] = useState([]);

    const [ page, setPage ] = useState(0);
    const [ rowsPerPage, setRowsPerPage ] = useState(10);

    const [ loading, setLoading ] = useState(false);

    const getArticles = () => {
        setLoading(true);

        api.get(`/admin/article`)
            .then(res => setArticles(res.articles))
            .finally(() => setLoading(false));
    }

    useEffect(() => {
        getArticles();
    }, []);

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    };

    const handleChangeRowsPerPage = event => {
        setRowsPerPage(event.target.value)
    };

    const getPublishText = a => {

        if (a.status === 'published') {
            return 'Published On: ' + DateTime.fromISO(a.published_at).toLocaleString(DateTime.DATETIME_SHORT);
        }
        if (a.status === 'scheduled') {
            return 'Scheduled For: ' + DateTime.fromISO(a.published_at).toLocaleString(DateTime.DATETIME_SHORT);
        }

        return null;
    }

    const getChipClass = a => {
        if (a.status === 'published') return classes.published;
        if (a.status === 'scheduled') return classes.scheduled;
        return classes.draft;
    }

    return (
        <div className={classes.container}>
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid container alignItems="center" spacing={3} className={classes.marginBottom}>
                    <Grid item>
                        <Typography variant="h4" component="h2">Articles</Typography>
                    </Grid>
                    <Grid item xs={true}/>
                    <Grid item xs={false}>
                        <Tooltip title="New Article">
                            <Button
                                aria-label="New Article"
                                variant="contained"
                                component={RouterLink}
                                to="/admin/articles/0"
                                startIcon={<AddIcon />}
                                color="primary"
                                >
                                New Article
                            </Button>
                        </Tooltip>
                    </Grid>
                </Grid>
                <Divider />
                {articles.length ? (
                    <List>
                    {articles.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map( a => (
                        <ListItem
                            alignItems="flex-start"
                            key={a.id}
                            button
                            component={RouterLink}
                            to={`/admin/articles/${a.id}`}
                            >
                            <Grid
                                container
                                spacing={3}
                                alignItems="center"
                                justify="space-between"
                                wrap="nowrap"
                                >
                                <Grid item>
                                    <Typography
                                        component="h2"
                                        color="textPrimary"
                                        variant="h6"
                                        >
                                        {a.title}
                                    </Typography>
                                    <Typography
                                        component="div"
                                        color="textSecondary"
                                        variant="body2"
                                        gutterBottom
                                        dangerouslySetInnerHTML={{__html: a.excerpt}}
                                        >
                                    </Typography>
                                    <Typography
                                        color="primary"
                                        variant="subtitle2"
                                        gutterBottom
                                        >
                                        <span className={classes.marginRight}>
                                            {'Last Updated: ' + DateTime.fromISO(a.updated_at).toLocaleString(DateTime.DATETIME_SHORT)}
                                        </span>
                                        <span>
                                            {getPublishText(a)}
                                        </span>
                                    </Typography>
                                </Grid>
                                <Grid item className={classes.nowrap}>
                                    <Chip
                                        label={a.status}
                                        className={getChipClass(a)}
                                    />
                                    <Chip
                                        label={a.group ? a.group.title : 'All Users'}
                                        color="primary"
                                    />
                                </Grid>
                            </Grid>
                        </ListItem>
                    ))}
                </List>
                ) : (
                    <Typography align="center" color="textSecondary" variant="subtitle2" className={classes.empty}>
                        You don't have any articles yet. Create your first article <Link component={RouterLink} color="primary" to="/admin/articles/0">here</Link>.
                    </Typography>
                )}
                <Divider />
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    count={articles.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onChangePage={handleChangePage}
                    onChangeRowsPerPage={handleChangeRowsPerPage}
                    component="div"
                />
            </Paper>
        </div>
    );
}
